import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Calendar, Eye, Edit, Trash2 } from 'lucide-react';
import { Card, Table, Badge, ConfirmModal } from '@/components/common';
import { UserListToolbar } from '@/components/users/UserListToolbar';
import { formatDate, getInitials } from '@/utils/format';
import { userService } from '@/services/userService';
import type { User } from '@/types';

export function UsersPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [error, setError] = useState('');

  // Fetch users from API
  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchQuery]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await userService.getUsers({
        page: currentPage,
        per_page: 10,
        search: searchQuery || undefined,
      });
      setUsers(response.data);
      setTotalUsers(response.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(filteredUsers.map((u) => u.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter((id) => id !== userId));
    }
  };

  const handleBulkDelete = () => {
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      if (userToDelete) {
        await userService.deleteUser(userToDelete);
      } else {
        await userService.bulkDeleteUsers(selectedUsers);
      }
      setSelectedUsers([]);
      setUserToDelete(null);
      setDeleteModalOpen(false);
      fetchUsers(); // Refresh list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete users');
      console.error('Delete error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (userId: string) => {
    navigate(`/users/${userId}`);
  };

  const handleEditUser = (userId: string) => {
    navigate(`/users/${userId}/edit`);
  };

  const handleDeleteUser = (userId: string) => {
    setUserToDelete(userId);
    setDeleteModalOpen(true);
  };

  const columns = [
    {
      key: 'select',
      title: (
        <input
          type="checkbox"
          checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-2 focus:ring-primary-500"
        />
      ),
      width: '50px',
      render: (_: any, user: User) => (
        <input
          type="checkbox"
          checked={selectedUsers.includes(user.id)}
          onChange={(e) => handleSelectUser(user.id, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-2 focus:ring-primary-500"
        />
      ),
    },
    {
      key: 'user',
      title: 'User',
      render: (_: any, user: User) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-medium">
            {getInitials(user.name)}
          </div>
          <div>
            <p className="font-medium text-secondary-900">{user.name}</p>
            <div className="flex items-center gap-1 text-xs text-secondary-500">
              <Mail className="w-3 h-3" />
              {user.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'roles',
      title: 'Roles',
      render: (_: any, user: User) => (
        <div className="flex flex-wrap gap-1">
          {user.roles && user.roles.length > 0 ? (
            user.roles.map((role) => (
              <Badge key={role.id} variant="default" size="sm">
                {role.name}
              </Badge>
            ))
          ) : (
            <span className="text-sm text-secondary-500">No roles</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_: any, user: User) =>
        user.email_verified_at ? (
          <Badge variant="success">Verified</Badge>
        ) : (
          <Badge variant="warning">Pending</Badge>
        ),
    },
    {
      key: 'created',
      title: 'Joined',
      render: (_: any, user: User) => (
        <div className="flex items-center gap-1 text-sm text-secondary-600">
          <Calendar className="w-4 h-4" />
          {formatDate(user.created_at, 'MMM d, yyyy')}
        </div>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: '100px',
      render: (_: any, user: User) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewUser(user.id);
            }}
            className="p-1.5 hover:bg-secondary-100 rounded transition-colors"
            title="View"
          >
            <Eye className="w-4 h-4 text-secondary-600" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEditUser(user.id);
            }}
            className="p-1.5 hover:bg-secondary-100 rounded transition-colors"
            title="Edit"
          >
            <Edit className="w-4 h-4 text-secondary-600" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteUser(user.id);
            }}
            className="p-1.5 hover:bg-red-100 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-secondary-900">Users</h1>
        <p className="text-secondary-600 mt-1">
          Manage user accounts and permissions
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Toolbar */}
      <UserListToolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCount={selectedUsers.length}
        onBulkDelete={handleBulkDelete}
        onExport={() => console.log('Export users')}
        onCreateNew={() => navigate('/users/create')}
      />

      {/* Users Table */}
      <Card padding="none">
        <Table
          data={filteredUsers}
          columns={columns}
          onRowClick={(user) => handleViewUser(user.id)}
          loading={loading}
          emptyMessage="No users found"
        />
      </Card>

      {/* Pagination Info */}
      <div className="flex items-center justify-between text-sm text-secondary-600">
        <span>
          Showing {filteredUsers.length} of {totalUsers} users
        </span>
        {totalUsers > 10 && (
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded hover:bg-secondary-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-3 py-1">Page {currentPage}</span>
            <button
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={filteredUsers.length < 10}
              className="px-3 py-1 border rounded hover:bg-secondary-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Users"
        description={
          userToDelete
            ? 'Are you sure you want to delete this user? This action cannot be undone.'
            : `Are you sure you want to delete ${selectedUsers.length} users? This action cannot be undone.`
        }
        confirmText="Delete"
        variant="danger"
        loading={loading}
      />
    </div>
  );
}
